import React, { useState } from 'react';
import { Reorder } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import type { Deliverable } from '../../types/portal';
import { usePortal } from '../../contexts/PortalContext';
import { AddDeliverable } from '../../components/portal/AddDeliverable';
import { DeliverableAdmin } from '../../components/portal/DeliverableAdmin';
import { ProjectFields } from '../../components/portal/ProjectFields';
import { projectStatus } from '../../utils/status';
import { ClientLink } from '../../components/portal/ClientLink';
import { PortalTeam } from '../../components/portal/PortalTeam';
import { ProjectOptions } from '../../components/portal/ProjectOptions';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD } from '../../components/portal/adminStyles';
export function ProjectAdmin() {
  const {
    activeProject,
    goPortal,
    // duplicateProject stays available in context for a future project
    // options menu — it is simply not a prominent action here.
    saveAsTemplate,
    startPreview,
    archiveProject,
    reopenProject,
    setDeliverableOrder,
    notify
  } = usePortal();
  const [editing, setEditing] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [carryingId, setCarryingId] = useState<string | null>(null);
  const project = activeProject;
  const total = project.deliverables.length;
  const hidden = project.deliverables.filter(item => !item.visibleToClient).length;
  function saveTemplate(event: React.FormEvent) {
    event.preventDefault();
    const name = templateName.trim();
    if (!name) return;
    saveAsTemplate(name);
    setTemplateName('');
    setSavingTemplate(false);
    notify(`Template saved · ${name}`);
  }
  return <div data-id="element-691" data-mp="d2k4hwu1z77e">
      <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-692" data-mp="85tn966ii5ke">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-693" data-mp="27da4ukj0thsx" />
        Client Projects
      </button>

      {/* The project itself is the anchor; everything else sits quietly under it. */}
      <header className="mt-8 border-b border-ruleDark pb-9" data-id="element-694" data-mp="2lct0idunb0oc">
        <p className="label text-paper/40" data-id="element-695" data-mp="2ldb7a4exoxai">{project.client}</p>
        <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-696" data-mp="2qal1dxd9px4o">
          {project.name}
        </h1>
        <p className="label mt-6 text-paper/40" data-id="element-697" data-mp="2be3ubic434nx">
          {project.type} · {projectStatus(project)} · {total} deliverable{total === 1 ? '' : 's'} ·
          Last updated {project.updated}
          {project.archived ? ' · Archived' : ''}
        </p>

        <ClientLink slug={project.slug} projectId={project.id} data-id="element-698" data-mp="14yjn2erxywca">
          <button type="button" onClick={startPreview} className={BUTTON_GHOST} data-id="element-699" data-mp="14vx65oj5v4he">
            Preview as Client
          </button>
        </ClientLink>
      </header>

      {/* The production work leads the page. */}
      <section aria-labelledby="deliverables-admin" className="mt-14" data-id="element-700" data-mp="3s6h1wf8quqw2">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-701" data-mp="3s4mp5o7mm532">
          <h2 id="deliverables-admin" className="label m-0 text-paper/40" data-id="element-702" data-mp="3o3az5hkubhuc">
            Deliverables
          </h2>
          {hidden > 0 && <p className="label-sm m-0 text-paper/35" data-id="element-703" data-mp="2vhj5kslcikws">{hidden} hidden from client</p>}
        </div>

        {total === 0 ? <p className="border-b border-ruleDark py-10 font-display text-xl text-paper/40" data-id="element-704" data-mp="39965r3iesdku">
            Nothing added yet.
          </p> : <Reorder.Group as="ul" axis="y" values={project.deliverables} onReorder={(next: Deliverable[]) => setDeliverableOrder(next.map(item => item.id))}
      /* A recessed field while a row is lifted, so the empty slot reads as a gap. */ className={['m-0 list-none p-0 transition-colors duration-150 ease-out', carryingId ? 'bg-paper/[0.02]' : ''].join(' ')} data-id="element-705" data-mp="2gqsdrkjr7lto">
            {project.deliverables.map(item => <DeliverableAdmin key={item.id} deliverable={item} onLift={setCarryingId} data-id="element-706" data-mp="345f0ki9autl2" />)}
          </Reorder.Group>}

        <AddDeliverable data-id="element-707" data-mp="3d4ikfogajd0m" />
      </section>

      <PortalTeam data-id="element-708" data-mp="3d3h87gnjgkhb" />

      {/* Occasional project-level actions, deliberately quiet. */}
      <section aria-labelledby="project-settings" className="mt-16 border-t border-ruleDark pt-8" data-id="element-709" data-mp="3i4wu1axjiv0r">
        <h2 id="project-settings" className="label m-0 text-paper/40" data-id="element-710" data-mp="3n1x2fshv49v1">
          Project Settings
        </h2>

        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-711" data-mp="1nr5lkeqy6h6w">
          <button type="button" onClick={() => setEditing(current => !current)} className={BUTTON_QUIET} data-id="element-712" data-mp="1so5ju3hk2fng">
            {editing ? 'Close Editor' : 'Edit Project'}
          </button>
          <button type="button" onClick={() => setSavingTemplate(current => !current)} className={BUTTON_QUIET} data-id="element-713" data-mp="1gd4u0sjlxrlz">
            Save as Template
          </button>
          {project.archived ? <button type="button" onClick={() => {
          reopenProject(project.id);
          notify('Project reopened · back in Active Projects');
        }} className={BUTTON_QUIET} data-id="element-714" data-mp="2440fl7xgzqks">
              Reopen Project
            </button> : <button type="button" onClick={() => setConfirmArchive(true)} className={BUTTON_QUIET} data-id="element-715" data-mp="1p7sve7m2ap5i">
              Archive Project
            </button>}
        </div>

        {confirmArchive && !project.archived && <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-716" data-mp="dqb2y49xlbya">
            <p className="font-display text-2xl leading-snug" data-id="element-717" data-mp="31yuonv59tckr">Archive this project?</p>
            <p className="label-sm mt-4 leading-relaxed text-paper/40" data-id="element-718" data-mp="35zodcqqjevep">
              It will be removed from Active Projects but its project history and data will be
              retained.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-719" data-mp="2wxx5pgcfsei7">
              <button type="button" onClick={() => {
            archiveProject();
            setConfirmArchive(false);
            notify('Project archived · nothing deleted');
            goPortal('projects');
          }} className={BUTTON_SOLID} data-id="element-720" data-mp="2ia2ey5hzok7r">
                Archive Project
              </button>
              <button type="button" onClick={() => setConfirmArchive(false)} className={BUTTON_QUIET} data-id="element-721" data-mp="1zay5wwqg6qg3">
                Cancel
              </button>
            </div>
          </div>}

        {savingTemplate && <form onSubmit={saveTemplate} className="mt-8 max-w-md" data-id="element-722" data-mp="1kq2iippkvpp3">
            <label className="block" data-id="element-723" data-mp="1go8jsmyy0vrk">
              <span className="label block text-paper/40" data-id="element-724" data-mp="1umfa9i1kbth7">Template Name</span>
              <input value={templateName} onChange={event => setTemplateName(event.target.value)} placeholder="e.g. 6-Episode Filmed Podcast" autoFocus className={FIELD} data-id="element-725" data-mp="1ukcai6alylza" />
            </label>
            <div className="mt-6 flex items-center gap-6" data-id="element-726" data-mp="1zl09pf8zq94l">
              <button type="submit" className={BUTTON_SOLID} data-id="element-727" data-mp="2dd6g49mcd1qu">
                Save Template
              </button>
              <button type="button" onClick={() => setSavingTemplate(false)} className={BUTTON_QUIET} data-id="element-728" data-mp="28ev7213vev9o">
                Cancel
              </button>
            </div>
          </form>}

        {editing && <ProjectFields onClose={() => setEditing(false)} data-id="element-729" data-mp="12zxh4ei9qx9t" />}
      </section>

      <ProjectOptions data-id="element-730" data-mp="1321jut4flq8i" />
    </div>;
}